import { ILongRunningAction, LongRunningActionState, LongRunningActionType } from '../models/ILongRunningAction';
import { Module } from '../Module';
import { IRebuildSearchIndexResult, RebuildSearchIndexRequest } from '../ServerApi/requests/RebuildSearchIndexRequest';
import { LongRunningActionService } from './LongRunningActionService';
import { IPoller } from './PollService';
import { ErrorReason, ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

export class RebuildSearchIndexService {

  private rebuildSearchIndexPoller: IPoller<ILongRunningAction>;
  private rebuildSearchIndexPending: boolean;
  private rebuildSearchIndexPercent: number;

  public static $inject = [
    '$translate',
    'ServerService',
    'LongRunningActionService',
    'ToasterService'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private server: ServerService,
    private lrActionService: LongRunningActionService,
    private toasterService: ToasterService
  ) {
    this.rebuildSearchIndexPercent = 0;
    this.rebuildSearchIndexPending = false;
  }

  public handleCurrentJob(): ng.IPromise<any> {
    return this.lrActionService.getLastByType(LongRunningActionType.RebuildSearchIndex)
      .then((action: ILongRunningAction) => this.handleRebuildSearchIndexStatus(action));
  }

  public getRebuildSearchIndexPercent(): number {
    return this.rebuildSearchIndexPercent;
  }

  public getRebuildSearchIndexPending(): boolean {
    return this.rebuildSearchIndexPending;
  }

  public rebuildSearchIndex(): ng.IPromise<any> {
    this.rebuildSearchIndexPercent = 0;
    return this.server.sendRequest(new RebuildSearchIndexRequest())
      .then((result: IRebuildSearchIndexResult) => {
        const existingRebuild = !result.actionId;
        if (!existingRebuild) {
          this.trackRebuildSearchIndexProgress(result.actionId);
        } else {
          this.getRebuildSearchIndexStatus();
        }
      });
  }

  public cancelPoll(): void {
    if (this.rebuildSearchIndexPoller) {
      this.rebuildSearchIndexPoller.cancel();
    }
    this.rebuildSearchIndexPercent = 0;
    this.rebuildSearchIndexPending = false;
  }

  private trackRebuildSearchIndexProgress(actionId: string): void {
    const completeFn = (action: ILongRunningAction) => {
      if (action && action.succeeded === false) {
        failFn(action && action.response && action.response.errors);
      }
      else {
        this.rebuildSearchIndexPercent = 100;
        this.toasterService.pop({ type: ToastType.Info, text: this.$translate.instant('rebuildSearchIndexSuccess')});
      }
    };
    const notifyFn = (action: ILongRunningAction) => {
      this.rebuildSearchIndexPercent = action.percentComplete;
    };
    const failFn = (reason: ErrorReason) => {
      if (reason !== ErrorReason.Canceled) {
      this.toasterService.pop({ type: ToastType.Error, text: this.$translate.instant('rebuildSearchIndexError') });
      }
    };
    if (actionId) {
      this.rebuildSearchIndexPending = true;
      this.rebuildSearchIndexPoller = this.lrActionService.trackAsyncJobProgress(actionId);
      this.rebuildSearchIndexPoller.promise
        .then(completeFn, failFn, notifyFn)
        .finally(() => this.rebuildSearchIndexPending = false);
    } else {
      failFn(ErrorReason.Unknown);
    }
  }

  private getRebuildSearchIndexStatus(): void {
    this.lrActionService.getLastByType(LongRunningActionType.RebuildSearchIndex)
      .then((action: ILongRunningAction) => this.handleRebuildSearchIndexStatus(action));
  }

  private handleRebuildSearchIndexStatus(action: ILongRunningAction): void {
    if(action && action.state !== LongRunningActionState.Complete) {
      this.rebuildSearchIndexPercent = action.percentComplete;
      this.trackRebuildSearchIndexProgress(action.id);
    }
  }

}

Module.instance().service('RebuildSearchIndexService', RebuildSearchIndexService);
